/**
 * Button Component Styles
 * Variants matching project theme configuration
 */

import { mode } from '../../system/color-mode';

export const buttonStyles = {
  // Base styles applied to all buttons
  baseStyle: {
    borderRadius: '16px',
    boxShadow: '45px 76px 113px 7px rgba(112, 144, 176, 0.08)',
    transition: '.25s all ease',
    boxSizing: 'border-box',
    fontWeight: 'medium',
    lineHeight: '1.2',
    _focus: {
      boxShadow: 'none',
    },
    _active: {
      boxShadow: 'none',
    },
    _disabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  },

  // Size variants
  sizes: {
    lg: {
      h: '48px',
      minW: '48px',
      fontSize: 'lg',
      px: '24px',
    },
    md: {
      h: '40px',
      minW: '40px',
      fontSize: 'md',
      px: '16px',
    },
    sm: {
      h: '32px',
      minW: '32px',
      fontSize: 'sm',
      px: '12px',
    },
    xs: {
      h: '24px',
      minW: '24px',
      fontSize: 'xs',
      px: '8px',
    },
  },

  // Visual variants
  variants: {
    // Solid (default Chakra)
    solid: (props) => ({
      bg: `${props.colorScheme || 'gray'}.500`,
      color: 'white',
      _hover: {
        bg: `${props.colorScheme || 'gray'}.600`,
        _disabled: {
          bg: `${props.colorScheme || 'gray'}.500`,
        },
      },
      _active: {
        bg: `${props.colorScheme || 'gray'}.700`,
      },
    }),

    // Outline
    outline: (props) => ({
      border: '1px solid',
      borderColor: `${props.colorScheme || 'gray'}.500`,
      color: `${props.colorScheme || 'gray'}.500`,
      bg: 'transparent',
      borderRadius: '16px',
      _hover: {
        bg: `${props.colorScheme || 'gray'}.50`,
      },
      _active: {
        bg: `${props.colorScheme || 'gray'}.100`,
      },
    }),

    // Ghost
    ghost: (props) => ({
      color: `${props.colorScheme || 'gray'}.500`,
      bg: 'transparent',
      _hover: {
        bg: `${props.colorScheme || 'gray'}.50`,
      },
      _active: {
        bg: `${props.colorScheme || 'gray'}.100`,
      },
    }),

    // Link
    link: (props) => ({
      color: `${props.colorScheme || 'brand'}.500`,
      bg: 'transparent',
      height: 'auto',
      padding: 0,
      _hover: {
        textDecoration: 'underline',
      },
      _active: {
        color: `${props.colorScheme || 'brand'}.700`,
      },
    }),

    // Unstyled
    unstyled: {
      bg: 'none',
      color: 'inherit',
      display: 'inline',
      lineHeight: 'inherit',
      m: 0,
      p: 0,
    },

    // ==================== PROJECT CUSTOM VARIANTS ====================

    // Brand (primary action)
    brand: (props) => ({
      bg: mode('brand.500', 'brand.400')(props),
      color: 'white',
      _focus: {
        bg: mode('brand.500', 'brand.400')(props),
      },
      _active: {
        bg: mode('brand.500', 'brand.400')(props),
      },
      _hover: {
        bg: mode('brand.600', 'brand.400')(props),
      },
    }),

    // Dark Brand
    darkBrand: (props) => ({
      bg: mode('brand.900', 'brand.400')(props),
      color: 'white',
      _focus: {
        bg: mode('brand.900', 'brand.400')(props),
      },
      _active: {
        bg: mode('brand.900', 'brand.400')(props),
      },
      _hover: {
        bg: mode('brand.800', 'brand.400')(props),
      },
    }),

    // Light Brand
    lightBrand: (props) => ({
      bg: mode('#F2EFFF', 'whiteAlpha.100')(props),
      color: mode('brand.500', 'white')(props),
      _focus: {
        bg: mode('#F2EFFF', 'whiteAlpha.100')(props),
      },
      _active: {
        bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
      },
      _hover: {
        bg: mode('secondaryGray.400', 'whiteAlpha.200')(props),
      },
    }),

    // Light
    light: (props) => ({
      bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
      color: mode('secondaryGray.900', 'white')(props),
      _focus: {
        bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
      },
      _active: {
        bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
      },
      _hover: {
        bg: mode('secondaryGray.400', 'whiteAlpha.200')(props),
      },
    }),

    // Action
    action: (props) => ({
      fontWeight: '500',
      borderRadius: '50px',
      bg: mode('secondaryGray.300', 'brand.400')(props),
      color: mode('brand.500', 'white')(props),
      _focus: {
        bg: mode('secondaryGray.300', 'brand.400')(props),
      },
      _active: { 
        bg: mode('secondaryGray.300', 'brand.400')(props),
      },
      _hover: {
        bg: mode('secondaryGray.200', 'brand.400')(props),
      },
    }),

    // Setup
    setup: (props) => ({
      fontWeight: '500',
      borderRadius: '50px',
      bg: mode('transparent', 'brand.400')(props),
      border: mode('1px solid', '0px solid')(props),
      borderColor: mode('secondaryGray.400', 'transparent')(props),
      color: mode('secondaryGray.900', 'white')(props),
      _focus: {
        bg: mode('transparent', 'brand.400')(props),
      },
      _active: { 
        bg: mode('transparent', 'brand.400')(props),
      },
      _hover: {
        bg: mode('secondaryGray.100', 'brand.400')(props),
      },
    }),
  },

  // Default props
  defaultProps: {
    variant: 'solid',
    size: 'md',
    colorScheme: 'gray',
  },
};

export default buttonStyles;
